"use client";

import { Calendar, User, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function getInitials(name?: string) {
  if (!name) return "AU";
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="h-6 w-32 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-5 w-1/2 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-96 w-full bg-muted/40 animate-pulse rounded-lg" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/40 animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted/40 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted/40 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Unable to load this article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Article not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link
          to="/TheBlueprintPage"
          className="inline-flex items-center gap-2 text-sm text-foreground hover:text-foreground/70 transition-colors font-default"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Insights</span>
        </Link>
      </div>

      {}
      {categoryName && (
        <div className="mb-6">
          <span className="inline-block px-3 py-1 text-xs uppercase tracking-widest font-default border border-border text-foreground rounded-md">
            {categoryName}
          </span>
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight mb-6">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-10">
        {post.author && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{post.author.name}</span>
          </div>
        )}
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(post.date)}</span>
          </div>
        )}
      </div>

      <Separator className="mb-10" />

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      {post.excerpt && (
        <div
          className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-12 italic border-l-2 border-foreground/70 pl-6"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-foreground leading-relaxed [&_p]:mb-6 [&_p]:text-base [&_p]:md:text-lg [&_h2]:font-bold [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-foreground [&_h3]:font-bold [&_h3]:text-2xl [&_h3]:mt-10 [&_h3]:mb-3 [&_h3]:text-foreground [&_a]:text-foreground/70 [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-2 [&_blockquote]:border-foreground/70 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_li]:mb-2 [&_img]:rounded-lg [&_img]:my-8"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      <Separator className="my-16" />

      {}
      {post.author && (
        <Card className="bg-card border border-border shadow-md rounded-lg">
          <CardContent className="p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <Avatar className="w-20 h-20 rounded-lg">
                {post.author.avatar && post.author.avatar["96"] ? (
                  <AvatarImage
                    src={post.author.avatar["96"]}
                    alt={post.author.name}
                    className="rounded-lg"
                  />
                ) : null}
                <AvatarFallback className="rounded-lg bg-secondary text-secondary-foreground font-default text-lg">
                  {getInitials(post.author.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-default mb-2">
                  Author
                </p>
                <h3 className="font-default font-bold text-2xl text-foreground mb-3">
                  {post.author.name}
                </h3>
                <p className="text-muted-foreground font-default leading-relaxed">
                  AI Automation expert at AutolexaAi, dedicated to helping businesses scale through intelligent workflow integration and smart technology.
                </p>
                {post.author.link && (
                  <Link
                    to={post.author.link}
                    className="inline-flex items-center gap-2 mt-4 text-sm text-foreground/70 hover:underline font-default"
                  >
                    <span>View more posts</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsConsumer({ currentPostId }: { currentPostId?: string }) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="h-56 bg-muted/40 animate-pulse rounded-lg" />
            <div className="h-5 w-3/4 bg-muted/40 animate-pulse rounded-md" />
            <div className="h-4 w-1/2 bg-muted/40 animate-pulse rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  const filtered = posts.filter((p) => p.id !== currentPostId).slice(0, 3);

  if (filtered.length === 0) {
    return (
      <p className="text-muted-foreground text-center font-default">No other articles available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {filtered.map((post, i) => (
        <article data-index={i} key={post.id} className="group">
          <Link to={post.link || "#"} className="block">
            {post.featuredImage && (
              <div className="overflow-hidden rounded-lg mb-5 aspect-[4/3] shadow-sm">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            {post.categories && post.categories.length > 0 && (
              <p className="text-xs uppercase tracking-widest text-foreground/70 font-default mb-3">
                {post.categories[0].name}
              </p>
            )}
            <h3 className="font-default font-bold text-xl md:text-2xl text-foreground leading-snug mb-3 group-hover:text-foreground/70 transition-colors">
              {post.title}
            </h3>
            {post.date && (
              <p className="text-sm text-muted-foreground font-default">{formatDate(post.date)}</p>
            )}
          </Link>
        </article>
      ))}
    </div>
  );
}

function RelatedPostsWrapper() {
  const { posts } = usePosts();
  const currentId = posts && posts.length > 0 ? posts[0].id : undefined;

  const relatedQueryParams = { post_type: "post", per_page: 4 };
  const relatedWpQuery = new WP_Query(relatedQueryParams);

  return (
    <div className="border-t border-border pt-16 mt-20">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-foreground/70 font-default mb-3">
              Explore More
            </p>
            <h2 className="font-default font-bold text-3xl md:text-4xl text-foreground tracking-tight">
              Related Insights
            </h2>
          </div>
          <Link to="/TheBlueprintPage">
            <Button variant="outline" className="font-default rounded-lg">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{ post_type: "post", per_page: 4 }}>
          <RelatedPostsConsumer currentPostId={currentId} />
        </PostsProvider>
      </div>
    </div>
  );
}

export default function Article() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="507"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
          <RelatedPostsWrapper />
        </PostsProvider>
      </div>
    </section>
  );
}